import React, { useState, useContext, useEffect, useRef } from 'react';
import { LanguageContext } from '../context/LanguageContext';
import { youtubeReelsData } from '../data/youtubeReelsData';
import './CompactReelsCarousel.css';

// Import Material Icons
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PauseIcon from '@mui/icons-material/Pause';
import MovieIcon from '@mui/icons-material/Movie';
import SwipeVerticalIcon from '@mui/icons-material/SwipeVertical';

const CompactReelsCarousel = () => {
  const { lang } = useContext(LanguageContext);
  const [showModal, setShowModal] = useState(false);
  const [currentReelIndex, setCurrentReelIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const reels = youtubeReelsData[lang] || youtubeReelsData['en'];

  const openModal = (index) => {
    // Set state first
    setCurrentReelIndex(index);
    setShowModal(true);
    setIsAutoPlaying(true);
    
    // Force immediate scroll to top
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Lock scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = '0';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.classList.add('modal-open');
    });
  };

  const closeModal = () => {
    // Unlock scroll
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.classList.remove('modal-open');
    
    // Close modal
    setShowModal(false);
    setIsAutoPlaying(false);
  };

  const handleNext = () => {
    setCurrentReelIndex((prev) => (prev + 1) % reels.length);
  };

  const handlePrev = () => {
    setCurrentReelIndex((prev) => (prev - 1 + reels.length) % reels.length);
  };

  useEffect(() => {
    if (isAutoPlaying && showModal) {
      const timer = setTimeout(() => {
        handleNext();
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [currentReelIndex, isAutoPlaying, showModal]);

  useEffect(() => {
    if (!showModal) return;

    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        handleNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showModal]);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isSwipe = Math.abs(distance) > 50;

    if (isSwipe) {
      if (distance > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.classList.remove('modal-open');
    };
  }, []);

  return (
    <>
      {/* Compact Reels Section */}
      <div className="compact-reels-container">
        <div className="compact-reels-header">
          <div className="header-icon">
            <span className="material-icons" style={{ fontSize: '3rem' }}>
              videocam
            </span>
          </div>
          <div className="header-content">
            <h3>{lang === 'mr' ? 'व्हिडिओ रील्स' : 'Video Reels'}</h3>
            <p>{lang === 'mr' ? 'आमचे कार्यक्रम पहा' : 'Watch Our Programs'}</p>
          </div>
        </div>

        <div 
          className="compact-carousel-wrapper"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className={`compact-carousel-track ${isPaused ? 'paused' : ''}`}>
            {reels.map((reel, index) => (
              <div
                key={`original-${reel.id}`}
                className="compact-reel-card"
                onClick={() => openModal(index)}
              >
                <div className="compact-reel-thumb">
                  <img src={reel.thumbnailUrl} alt={reel.title} />
                  <div className="compact-play-overlay">
                    <div className="compact-play-btn">
                      <span className="material-icons" style={{ fontSize: '2rem' }}>
                        play_arrow
                      </span>
                    </div>
                  </div>
                  <div className="compact-reel-badge">
                    <span className="material-icons" style={{ fontSize: '0.875rem', marginRight: '4px' }}>
                      movie
                    </span>
                    {lang === 'mr' ? 'रील' : 'Reel'}
                  </div>
                </div>
                <div className="compact-reel-title">{reel.title}</div>
              </div>
            ))}
            
            {reels.map((reel, index) => (
              <div
                key={`duplicate-${reel.id}`}
                className="compact-reel-card"
                onClick={() => openModal(index)}
              >
                <div className="compact-reel-thumb">
                  <img src={reel.thumbnailUrl} alt={reel.title} />
                  <div className="compact-play-overlay">
                    <div className="compact-play-btn">
                      <span className="material-icons" style={{ fontSize: '2rem' }}>
                        play_arrow
                      </span>
                    </div>
                  </div>
                  <div className="compact-reel-badge">
                    <span className="material-icons" style={{ fontSize: '0.875rem', marginRight: '4px' }}>
                      movie
                    </span>
                    {lang === 'mr' ? 'रील' : 'Reel'}
                  </div>
                </div>
                <div className="compact-reel-title">{reel.title}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="carousel-footer">
          <span className="scroll-hint">
            <span className="material-icons" style={{ fontSize: '1.25rem', verticalAlign: 'middle', marginRight: '4px' }}>
              touch_app
            </span>
            {lang === 'mr' ? 'कार्ड्स पाहण्यासाठी होवर करा' : 'Hover to pause & explore'}
          </span>
        </div>
      </div>

      {/* Instagram-Style Modal - PORTAL RENDERING */}
      {showModal && (
        <div className="reel-modal-portal">
          <div className="instagram-reels-overlay">
            <div className="modal-backdrop" onClick={closeModal}></div>
            
            <div 
              className="instagram-reels-modal"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              
              <button 
                className="instagram-close-btn" 
                onClick={closeModal}
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 28 }} />
              </button>

              <div className="instagram-counter">
                <MovieIcon sx={{ fontSize: 20 }} />
                <span>{currentReelIndex + 1} / {reels.length}</span>
              </div>

              <div className="instagram-video-wrapper">
                <div className="instagram-video-container">
                  <iframe
                    key={currentReelIndex}
                    src={`https://www.youtube.com/embed/${reels[currentReelIndex].videoId}?autoplay=1&rel=0&modestbranding=1&controls=1`}
                    title={reels[currentReelIndex].title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>

                <div className="instagram-video-info">
                  <h3 className="video-title">{reels[currentReelIndex].title}</h3>
                  <p className="video-description">{reels[currentReelIndex].description}</p>
                </div>
              </div>

              <button 
                className="instagram-nav-btn instagram-nav-prev" 
                onClick={handlePrev}
                aria-label="Previous"
              >
                <ChevronLeftIcon sx={{ fontSize: 36 }} />
              </button>
              
              <button 
                className="instagram-nav-btn instagram-nav-next" 
                onClick={handleNext}
                aria-label="Next"
              >
                <ChevronRightIcon sx={{ fontSize: 36 }} />
              </button>

              <div className="instagram-side-controls">
                <button 
                  className={`side-control-btn ${isAutoPlaying ? 'active' : ''}`}
                  onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                  title={isAutoPlaying ? 'Pause Auto-play' : 'Enable Auto-play'}
                >
                  {isAutoPlaying ? (
                    <PauseIcon sx={{ fontSize: 24 }} />
                  ) : (
                    <PlayArrowIcon sx={{ fontSize: 24 }} />
                  )}
                  <span className="control-label">
                    {isAutoPlaying ? (lang === 'mr' ? 'ऑटो' : 'Auto') : (lang === 'mr' ? 'प्ले' : 'Play')}
                  </span>
                </button>

                <div className="instagram-progress-dots">
                  {reels.map((_, index) => (
                    <button
                      key={index}
                      className={`progress-dot-btn ${index === currentReelIndex ? 'active' : ''}`}
                      onClick={() => setCurrentReelIndex(index)}
                      aria-label={`Go to reel ${index + 1}`}
                    >
                      <span className="dot"></span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="instagram-swipe-indicator">
                <SwipeVerticalIcon sx={{ fontSize: 32, marginBottom: '8px' }} />
                <p>{lang === 'mr' ? 'स्वाइप करा' : 'Swipe Up/Down'}</p>
              </div>

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompactReelsCarousel;